import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from './useMultiTenant';

export interface AccountTransaction {
  id: string;
  payment_date: string;
  payment_value: number;
  payment_method: string;
  type: 'income' | 'expense';
  description: string;
  /** R-FIN.1: 'expense'/'revenue' = lançamento direto na conta (tabelas expenses/revenues) */
  reference_type: 'receivable' | 'payable' | 'bank' | 'expense' | 'revenue';
  reference_id: string;
  customer_or_supplier_name?: string;
  notes?: string;
  /** 'ledger' = lançamento direto (sem título pago nem linha bancária) */
  source: 'system' | 'bank' | 'ledger';
  reconciliation_status?: string;
  reconciled_by?: string | null;
  ignore_reason?: string | null;
  transfer_pair_id?: string | null;
}

interface UseAccountTransactionsParams {
  accountId: string;
  startDate?: string;
  endDate?: string;
  type?: 'all' | 'income' | 'expense';
  source?: 'all' | 'system' | 'bank';
}

export const useAccountTransactions = ({
  accountId,
  startDate,
  endDate,
  type = 'all',
  source = 'all',
}: UseAccountTransactionsParams) => {
  const { companyId } = useMultiTenant();

  return useQuery({
    queryKey: ['account-transactions', accountId, companyId, startDate, endDate, type, source],
    queryFn: async (): Promise<AccountTransaction[]> => {
      if (!accountId || !companyId) return [];

      const transactions: AccountTransaction[] = [];

      // Fetch system transactions (receivable and payable payments)
      if (source === 'all' || source === 'system') {
        // Fusão do par conciliado (modelo Conta Azul): pós-conciliação o
        // movimento é um só e a linha do banco é a verdade — a linha system
        // do payment vinculado é suprimida do união. Para receivables o
        // vínculo vive no próprio payment (bank_transaction_id); para
        // payables só existe na direção reversa (bank_transactions.
        // payable_payment_id), daí a busca do Set abaixo (sem filtro de
        // data, para cobrir pares na borda do período).
        let reconciledPayableIds = new Set<string>();
        if (type === 'all' || type === 'expense') {
          const { data: linkedData, error: linkedError } = await supabase
            .from('bank_transactions')
            .select('payable_payment_id')
            .eq('financial_account_id', accountId)
            .eq('reconciliation_status', 'reconciled')
            .not('payable_payment_id', 'is', null);

          if (linkedError) {
            console.error('Error fetching reconciled payable links:', linkedError);
          } else {
            reconciledPayableIds = new Set(
              (linkedData || []).map((l: any) => l.payable_payment_id).filter(Boolean)
            );
          }
        }

        // Fetch receivable payments (income)
        if (type === 'all' || type === 'income') {
          let receivableQuery = supabase
            .from('receivable_payments')
            .select(`
              id,
              payment_date,
              payment_value,
              payment_method,
              notes,
              receivable_id,
              bank_transaction_id,
              accounts_receivable!inner(
                id,
                description,
                customer_id,
                customers(name)
              )
            `)
            .eq('financial_account_id', accountId);

          if (startDate) {
            receivableQuery = receivableQuery.gte('payment_date', startDate);
          }
          if (endDate) {
            receivableQuery = receivableQuery.lte('payment_date', endDate);
          }

          const { data: receivableData, error: receivableError } = await receivableQuery;

          if (receivableError) {
            console.error('Error fetching receivable payments:', receivableError);
          } else if (receivableData) {
            receivableData.forEach((payment: any) => {
              // Par conciliado: a linha do banco representa o movimento
              if (payment.bank_transaction_id) return;

              transactions.push({
                id: payment.id,
                payment_date: payment.payment_date,
                payment_value: payment.payment_value,
                payment_method: payment.payment_method,
                type: 'income',
                description: payment.accounts_receivable?.description || 'Recebimento',
                reference_type: 'receivable',
                reference_id: payment.receivable_id,
                customer_or_supplier_name: payment.accounts_receivable?.customers?.name,
                notes: payment.notes,
                source: 'system',
              });
            });
          }
        }

        // Fetch payable payments (expense)
        if (type === 'all' || type === 'expense') {
          let payableQuery = supabase
            .from('payable_payments')
            .select(`
              id,
              payment_date,
              payment_value,
              payment_method,
              notes,
              payable_id,
              accounts_payable!inner(
                id,
                description,
                supplier_id,
                suppliers(name)
              )
            `)
            .eq('financial_account_id', accountId);

          if (startDate) {
            payableQuery = payableQuery.gte('payment_date', startDate);
          }
          if (endDate) {
            payableQuery = payableQuery.lte('payment_date', endDate);
          }

          const { data: payableData, error: payableError } = await payableQuery;

          if (payableError) {
            console.error('Error fetching payable payments:', payableError);
          } else if (payableData) {
            payableData.forEach((payment: any) => {
              // Par conciliado: a linha do banco representa o movimento
              if (reconciledPayableIds.has(payment.id)) return;

              transactions.push({
                id: payment.id,
                payment_date: payment.payment_date,
                payment_value: payment.payment_value,
                payment_method: payment.payment_method,
                type: 'expense',
                description: payment.accounts_payable?.description || 'Pagamento',
                reference_type: 'payable',
                reference_id: payment.payable_id,
                customer_or_supplier_name: payment.accounts_payable?.suppliers?.name,
                notes: payment.notes,
                source: 'system',
              });
            });
          }
        }

        // R-FIN.1: lançamentos DIRETOS na conta (expenses/revenues) — tarifa de
        // transferência, quebra/sobra de caixa, despesa/receita avulsa. Movem o
        // saldo (trigger V2) mas não passavam pelo extrato. Dois cortes contra
        // duplicidade: (a) espelhos dos pagamentos já listados acima
        // (trg_sync_payment_to_expense/revenue: reference_code/reference_id
        // 'PAYPMT-'/'RCVPMT-', reference_type '*_payment'); (b) lançamento já
        // casado com linha bancária (bank_transaction_id) — a linha do banco é
        // a verdade, como nos payments.
        const isPaymentMirror = (row: any) => {
          const code = String(row.reference_code ?? '');
          const ref = String(row.reference_id ?? '');
          const rtype = String(row.reference_type ?? '');
          return (
            code.startsWith('PAYPMT-') || code.startsWith('RCVPMT-') ||
            ref.startsWith('PAYPMT-') || ref.startsWith('RCVPMT-') ||
            rtype === 'payable_payment' || rtype === 'receivable_payment'
          );
        };
        const LEDGER_COLUMNS =
          'id, competence_date, value, description, payment_method, transaction_number, reference_type, reference_id, reference_code, bank_transaction_id, notes';

        if (type === 'all' || type === 'expense') {
          let expenseQuery = supabase
            .from('expenses')
            .select(LEDGER_COLUMNS)
            .eq('financial_account_id', accountId)
            .is('deleted_at', null);
          if (startDate) expenseQuery = expenseQuery.gte('competence_date', startDate);
          if (endDate) expenseQuery = expenseQuery.lte('competence_date', endDate);

          const { data: expenseData, error: expenseError } = await expenseQuery;
          if (expenseError) {
            console.error('Error fetching direct expenses:', expenseError);
          } else if (expenseData) {
            (expenseData as any[]).forEach((row) => {
              if (row.bank_transaction_id) return;
              if (isPaymentMirror(row)) return;
              transactions.push({
                id: row.id,
                payment_date: row.competence_date,
                payment_value: Number(row.value) || 0,
                payment_method: row.payment_method || 'other',
                type: 'expense',
                description: row.description || 'Despesa',
                reference_type: 'expense',
                reference_id: row.id,
                notes: [row.transaction_number, row.notes].filter(Boolean).join(' · ') || undefined,
                source: 'ledger',
              });
            });
          }
        }

        if (type === 'all' || type === 'income') {
          let revenueQuery = supabase
            .from('revenues')
            .select(LEDGER_COLUMNS)
            .eq('financial_account_id', accountId)
            .is('deleted_at', null);
          if (startDate) revenueQuery = revenueQuery.gte('competence_date', startDate);
          if (endDate) revenueQuery = revenueQuery.lte('competence_date', endDate);

          const { data: revenueData, error: revenueError } = await revenueQuery;
          if (revenueError) {
            console.error('Error fetching direct revenues:', revenueError);
          } else if (revenueData) {
            (revenueData as any[]).forEach((row) => {
              if (row.bank_transaction_id) return;
              if (isPaymentMirror(row)) return;
              transactions.push({
                id: row.id,
                payment_date: row.competence_date,
                payment_value: Number(row.value) || 0,
                payment_method: row.payment_method || 'other',
                type: 'income',
                description: row.description || 'Receita',
                reference_type: 'revenue',
                reference_id: row.id,
                notes: [row.transaction_number, row.notes].filter(Boolean).join(' · ') || undefined,
                source: 'ledger',
              });
            });
          }
        }
      }

      // Fetch bank transactions
      if (source === 'all' || source === 'bank') {
        let bankQuery = supabase
          .from('bank_transactions')
          .select('*')
          .eq('financial_account_id', accountId);

        if (startDate) {
          bankQuery = bankQuery.gte('transaction_date', startDate);
        }
        if (endDate) {
          bankQuery = bankQuery.lte('transaction_date', endDate);
        }

        const { data: bankData, error: bankError } = await bankQuery;

        if (bankError) {
          console.error('Error fetching bank transactions:', bankError);
        } else if (bankData) {
          bankData.forEach((bt: any) => {
            // transaction_type é a fonte da direção (convenção da tabela:
            // value sempre positivo); fallback pelo sinal só se o campo faltar
            const isCredit = bt.transaction_type ? bt.transaction_type === 'credit' : bt.value > 0;
            
            // Apply type filter for bank transactions
            if (type === 'income' && !isCredit) return;
            if (type === 'expense' && isCredit) return;

            transactions.push({
              id: bt.id,
              payment_date: bt.transaction_date,
              payment_value: Math.abs(bt.value),
              payment_method: bt.transaction_type || 'bank',
              type: isCredit ? 'income' : 'expense',
              description: bt.description,
              reference_type: 'bank',
              reference_id: bt.id,
              customer_or_supplier_name: bt.counterpart_name || undefined,
              notes: bt.category || undefined,
              source: 'bank',
              reconciliation_status: bt.reconciliation_status,
              reconciled_by: bt.reconciled_by ?? null,
              ignore_reason: bt.ignore_reason ?? null,
              transfer_pair_id: bt.transfer_pair_id ?? null,
            });
          });
        }
      }

      // Sort by date descending
      transactions.sort((a, b) => 
        new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
      );

      return transactions;
    },
    enabled: !!accountId && !!companyId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useAccountTransactionsSummary = (transactions: AccountTransaction[]) => {
  const totalIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.payment_value, 0);

  const totalExpense = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.payment_value, 0);

  const balance = totalIncome - totalExpense;

  const systemCount = transactions.filter(t => t.source === 'system').length;
  const bankCount = transactions.filter(t => t.source === 'bank').length;

  return {
    totalIncome,
    totalExpense,
    balance,
    transactionCount: transactions.length,
    systemCount,
    bankCount,
  };
};
